/**
 * Space behind the planet: black, full of stars. Thousands of points far out, most of them faint
 * and a few bright, white with a touch of blue or gold, each twinkling a little. They turn with the
 * planet, so they wheel past as you walk. One draw call; their size is in screen pixels, so they
 * stay sharp on every phone.
 */
import * as THREE from 'three';

import { hash2 } from '@core/math';

/** Space is black; so are the page behind the canvas (tokens.css) and the native shell (capacitor.config.ts). */
export const SPACE = 0x000000;

/** How many stars, and how far out: beyond any planet and any zoom, within the camera's reach (800). */
const COUNT = 9000;
const DISTANCE = 300;
/** Star colours: mostly white, some blue, some gold. */
const WHITE = [1, 1, 1] as const;
const TINTS: readonly (readonly [number, number, number])[] = [
  WHITE,
  WHITE,
  WHITE,
  [0.8, 0.87, 1],
  [0.8, 0.87, 1],
  [1, 0.9, 0.74],
  [1, 0.8, 0.62],
];

export interface StarField {
  /** xyz per star, evenly spread over a sphere `DISTANCE` out. */
  positions: Float32Array;
  /** rgb per star, its brightness included. */
  colors: Float32Array;
  /** Its size in CSS pixels. */
  sizes: Float32Array;
  /** 0..1 per star: when and how fast it twinkles. */
  seeds: Float32Array;
}

/** The same sky every time: where every star is, how bright and big, what colour. */
export function starField(count = COUNT): StarField {
  const field: StarField = {
    positions: new Float32Array(count * 3),
    colors: new Float32Array(count * 3),
    sizes: new Float32Array(count),
    seeds: new Float32Array(count),
  };
  for (let i = 0; i < count; i++) {
    // an even spread over the sphere: height uniform, angle around uniform
    const y = 2 * hash2(i, 23) - 1;
    const around = hash2(i, 11) * Math.PI * 2;
    const r = Math.sqrt(1 - y * y);
    field.positions[i * 3] = r * Math.cos(around) * DISTANCE;
    field.positions[i * 3 + 1] = y * DISTANCE;
    field.positions[i * 3 + 2] = r * Math.sin(around) * DISTANCE;
    // most stars are faint and small, a few are bright
    const bright = Math.pow(hash2(i, 37), 3.2);
    const tint = TINTS[Math.floor(hash2(i, 41) * TINTS.length)] ?? WHITE;
    const light = 0.45 + 0.55 * bright;
    field.colors[i * 3] = tint[0] * light;
    field.colors[i * 3 + 1] = tint[1] * light;
    field.colors[i * 3 + 2] = tint[2] * light;
    field.sizes[i] = 1 + 2.4 * bright;
    field.seeds[i] = hash2(i, 53);
  }
  return field;
}

const VERTEX = /* glsl */ `
attribute vec3 tint;
attribute float size;
attribute float seed;
uniform float uTime;
uniform float uPixelRatio;
varying vec3 vTint;
void main() {
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  float twinkle = 0.8 + 0.2 * sin(uTime * (0.6 + 1.8 * fract(seed * 7.31)) + seed * 6.2832);
  vTint = tint * twinkle;
  gl_PointSize = size * uPixelRatio;
}`;

const FRAGMENT = /* glsl */ `
varying vec3 vTint;
void main() {
  vec2 p = gl_PointCoord * 2.0 - 1.0;
  float glow = max(0.0, 1.0 - dot(p, p));
  gl_FragColor = vec4(vTint * glow * glow, 1.0);
}`;

export interface Stars {
  readonly points: THREE.Points;
  /** Once a frame: turns with `turn` (the planet's), and twinkles; `seconds` held still keeps them still. */
  update(seconds: number, turn: THREE.Quaternion): void;
  /** Device pixels per CSS pixel, so stars keep their size on every screen and quality tier. */
  setPixelRatio(ratio: number): void;
}

export function createStars(): Stars {
  const field = starField();
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(field.positions, 3));
  geometry.setAttribute('tint', new THREE.BufferAttribute(field.colors, 3));
  geometry.setAttribute('size', new THREE.BufferAttribute(field.sizes, 1));
  geometry.setAttribute('seed', new THREE.BufferAttribute(field.seeds, 1));
  const uniforms = { uTime: { value: 0 }, uPixelRatio: { value: 1 } };
  const points = new THREE.Points(
    geometry,
    new THREE.ShaderMaterial({
      uniforms,
      vertexShader: VERTEX,
      fragmentShader: FRAGMENT,
      // added light: never darkens, and the planet in front hides them
      blending: THREE.AdditiveBlending,
      transparent: true,
      depthWrite: false,
    }),
  );
  points.name = 'stars';
  // first of what's see-through, behind the halo and the clouds; they fill the sky anyway
  points.renderOrder = -1;
  points.frustumCulled = false;
  return {
    points,
    update(seconds, turn) {
      points.quaternion.copy(turn);
      uniforms.uTime.value = seconds;
    },
    setPixelRatio(ratio) {
      uniforms.uPixelRatio.value = ratio;
    },
  };
}
